import type { ProductTableFilters } from '@/hooks/useProductTableConfig'

export const TOP_SELLER_QTY = 50
export const TOP_SELLER_REV = 5000
export const DEFAULT_SEGMENT_COLOR = '#6366f1'

export type SegmentField =
  | 'is_active' | 'has_image'
  | 'quantity_30d_min' | 'revenue_30d_min' | 'quantity_90d_min' | 'revenue_90d_min'
export type SegmentOp = 'eq' | 'gte'
export type SegmentCondition = { field: SegmentField; op: SegmentOp; value: number | boolean }
export type ProductSegmentRule = { match: 'all' | 'any'; conditions: SegmentCondition[] }

export type CustomSegment = {
  id: string
  name: string
  color: string
  rule: ProductSegmentRule
  pinned: boolean
}

export type PresetId =
  | 'all' | 'top-seller' | 'slow-movers' | 'no-packshot' | 'with-issues' | 'new-30d'

export type Preset = { id: PresetId; labelKey: string; disabled?: boolean }

export const PRESETS: Preset[] = [
  { id: 'all', labelKey: 'productSegments.presets.all' },
  { id: 'top-seller', labelKey: 'productSegments.presets.topSeller' },
  { id: 'slow-movers', labelKey: 'productSegments.presets.slowMovers', disabled: true },
  { id: 'no-packshot', labelKey: 'productSegments.presets.noPackshot' },
  { id: 'with-issues', labelKey: 'productsOverview.kpi.withIssues', disabled: true },
  { id: 'new-30d', labelKey: 'productSegments.presets.new30d' },
]

function isoMinusDays(days: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString().slice(0, 10)
}

export function presetFilters(id: PresetId): ProductTableFilters {
  switch (id) {
    case 'all':
      return {}
    case 'top-seller':
      return { quantity_30d_min: TOP_SELLER_QTY, revenue_30d_min: TOP_SELLER_REV }
    case 'no-packshot':
      return { has_image: false }
    case 'new-30d':
      return { synced_after: isoMinusDays(30) }
    default:
      return {}
  }
}

const NUMERIC_FIELDS = [
  'quantity_30d_min', 'revenue_30d_min', 'quantity_90d_min', 'revenue_90d_min',
] as const
type NumericSegmentField = (typeof NUMERIC_FIELDS)[number]

export function ruleToFilters(rule: ProductSegmentRule): ProductTableFilters {
  const f: ProductTableFilters = {}
  for (const c of rule.conditions) {
    if (c.field === 'is_active' && c.op === 'eq') f.is_active = Boolean(c.value)
    else if (c.field === 'has_image' && c.op === 'eq') f.has_image = Boolean(c.value)
    else if ((NUMERIC_FIELDS as readonly string[]).includes(c.field) && c.op === 'gte') {
      ;(f as Record<NumericSegmentField, number>)[c.field as NumericSegmentField] = Number(c.value)
    }
  }
  return f
}

export function filtersToRule(f: ProductTableFilters): ProductSegmentRule {
  const conditions: SegmentCondition[] = []
  if (f.is_active !== undefined) conditions.push({ field: 'is_active', op: 'eq', value: f.is_active })
  if (f.has_image !== undefined) conditions.push({ field: 'has_image', op: 'eq', value: f.has_image })
  for (const k of NUMERIC_FIELDS) {
    const v = f[k]
    if (v !== undefined) conditions.push({ field: k, op: 'gte', value: Number(v) })
  }
  return { match: 'all', conditions }
}
